import React from 'react';
import { LinkOnClickEvent } from '@doveit/lego';
import SideNavigationItem, { SideNavigationItemProps } from './parts/side-navigation-item/SideNavigationItem';

interface Item {
  id: string | number,
}

export interface SideNavigationProps {
  activeItemId?: Item['id'],
  onCommonClick?: (item: Item) => void,
  onSelectedItemChange?: (item: Item) => void,
}

export interface SideNavigationComposition {
  Item: typeof SideNavigationItemWithContext,
  SecondLevelSubItem: typeof SideNavigationSecondLevelSubItem,
  ThirdLevelSubItem: typeof SideNavigationThirdLevelSubItem,
  FourthLevelSubItem: typeof SideNavigationFourthLevelSubItem,
}

export interface SideNavigationItemWithContextProps extends SideNavigationItemProps {
  id: Item['id'],
}

export interface SideNavigationContextValue {
  activeItemId?: Item['id'],
  onItemClick?: (item: Item & {
    customAction?: (event: LinkOnClickEvent) => void,
  }) => (event: LinkOnClickEvent) => void,
}

export const SideNavigationContext = React.createContext<SideNavigationContextValue>({});

const SideNavigationItemWithContext: React.FC<SideNavigationItemWithContextProps> = ({
  id,
  onClick,
  secondLevel = false,
  thirdLevel = false,
  fourthLevel = false,
  highlighted = false,
  ...rest
}) => (
  <SideNavigationContext.Consumer>
    {({
      activeItemId,
      onItemClick,
    }) => (
      <>
        { !secondLevel && !thirdLevel && !fourthLevel && (
          <li>
            <SideNavigationItem
              {...rest}
              onClick={onItemClick({ id, customAction: onClick })}
              active={activeItemId === id}
              highlighted={highlighted}
            />
          </li>
        )}
        {secondLevel && (
          <SideNavigationItem
            {...rest}
            onClick={onItemClick({ id, customAction: onClick })}
            active={activeItemId === id}
            secondLevel
            highlighted={highlighted}
          />
        )}
        {thirdLevel && (
          <SideNavigationItem
            {...rest}
            onClick={onItemClick({ id, customAction: onClick })}
            active={activeItemId === id}
            thirdLevel
            highlighted={highlighted}
          />
        )}
        {fourthLevel && (
          <SideNavigationItem
            {...rest}
            onClick={onItemClick({ id, customAction: onClick })}
            active={activeItemId === id}
            fourthLevel
            highlighted={highlighted}
          />
        )}
      </>
    )}
  </SideNavigationContext.Consumer>
);

const SideNavigationSecondLevelSubItem: React.FC<SideNavigationItemWithContextProps> = (props) => (
  <SideNavigationItemWithContext secondLevel {...props} />
);

const SideNavigationThirdLevelSubItem: React.FC<SideNavigationItemWithContextProps> = (props) => (
  <SideNavigationItemWithContext thirdLevel {...props} />
);

const SideNavigationFourthLevelSubItem: React.FC<SideNavigationItemWithContextProps> = (props) => (
  <SideNavigationItemWithContext fourthLevel {...props} />
);

const SideNavigation: React.FC<SideNavigationProps> & SideNavigationComposition = ({
  activeItemId,
  onCommonClick,
  onSelectedItemChange,
  children,
}) => {
  const onItemClick: SideNavigationContextValue['onItemClick'] = React.useCallback(({ id, customAction }) => (event) => {
    if (onSelectedItemChange) {
      onSelectedItemChange({ id });
    }

    if (customAction) {
      customAction(event);
      return;
    }

    if (onCommonClick) {
      onCommonClick({ id });
    }
  }, [onCommonClick, onSelectedItemChange]);

  const contextValue: SideNavigationContextValue = React.useMemo(() => ({
    activeItemId,
    onItemClick,
  }), [activeItemId, onItemClick]);

  return (
    <SideNavigationContext.Provider value={contextValue}>
      <nav role="navigation">
        <ul>
          {children}
        </ul>
      </nav>
    </SideNavigationContext.Provider>
  );
};

SideNavigation.Item = SideNavigationItemWithContext;
SideNavigation.SecondLevelSubItem = SideNavigationSecondLevelSubItem;
SideNavigation.ThirdLevelSubItem = SideNavigationThirdLevelSubItem;
SideNavigation.FourthLevelSubItem = SideNavigationFourthLevelSubItem;

export default SideNavigation;
